import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCrefisaContext } from '@/app/contexts/CrefisaContext'
import { crefisaService } from '@/app/services/crefisaService'
import { useCrefisaStream } from '@/app/hooks/useCrefisaStream'
import { useEffect, useState } from 'react'
import { Simulacao } from '@/app/services/crefisaService/createSimulations'
import { useToast } from '@/view/components/ui/use-toast'
import { consentToBankAccount } from '@/app/services/crefisaService/consentToBankAccount'
import { DadoConsentimento } from '@/app/services/crefisaService/getConsent'

const getOffersModalSchema = z.object({})

type getOffersModalFormData = z.infer<typeof getOffersModalSchema>

interface UseOffersModalProps {
  onContinue?: () => void
}

export default function useOffersModal({ onContinue }: UseOffersModalProps) {
  const {
    simulationData,
    customerData,
    setCustomer,
    setProposal,
    setConsentBankAccount,
  } = useCrefisaContext();
  const { toast } = useToast();

  const [selectedInstallment, setSelectedInstallment] =
    useState<Simulacao | null>(null)
  const [manualRegistration, setManualRegistration] = useState('')
  const [manualRegistrationDigit, setManualRegistrationDigit] = useState('')
  const [netIncome, setNetIncome] = useState('')
  const [verifiedConsent, setVerifiedConsent] =
    useState<DadoConsentimento | null>(null)
  const [consentLoading, setConsentLoading] = useState(false)

  const getOffersModalForm = useForm<getOffersModalFormData>({
    resolver: zodResolver(getOffersModalSchema),
    mode: 'onChange',
  })

  const { handleSubmit } = getOffersModalForm

  const {
    data,
    steps,
    isStreaming: isFetching,
    isError,
    error,
    retry,
    cancel,
  } = useCrefisaStream({
    queryKey: [
      'crefisa-simulation',
      simulationData?.clientId,
      customerData?.cpf,
      simulationData?.isRefinancing ?? false,
    ],
    enabled: !!simulationData?.clientId && !!customerData?.cpf,
    request: ({ onStep, signal }) =>
      crefisaService.createSimulations(
        simulationData!.clientId!,
        {
          cpf: customerData!.cpf,
          isRefinancing: simulationData!.isRefinancing ?? false,
          phone: customerData!.phone,
        },
        { onStep, signal },
      ),
  })

  useEffect(() => {
    if (!isError) return

    const err = error as {
      message?: string
      mensagem?: string
    } | null

    toast({
      title: 'Erro ao gerar proposta',
      description:
        err?.mensagem ||
        err?.message ||
        'Não foi possível realizar a simulação.',
      variant: 'destructive',
    })
  }, [isError, error, toast])

  const borrowedValue = selectedInstallment?.valorSolicitado ?? 0

  const hasRenda = !!data?.renda
  const isNetIncomeIncomplete = !hasRenda && !netIncome

  const submitModal = handleSubmit(() => {
    if (!selectedInstallment) return

    if (requiresConsent && !isConsentAuthorized) {
      toast({
        title: 'Consentimento obrigatório',
        description:
          'É obrigatório realizar o consentimento para finalizar a proposta deste cliente.',
        variant: 'destructive',
      })
      return
    }

    setCustomer({
      income: {
        registration: data?.matricula ?? manualRegistration,
        registrationDigit: data?.digitoMatricula ?? manualRegistrationDigit,
        valueIncome: hasRenda ? data!.renda : Number(netIncome) / 100,
        codeBenefitSpecie: 0,
      },
    })

    setProposal({
      value: selectedInstallment.valorSolicitado,
      prazo: selectedInstallment.quantidadeParcelas,
      simulationId: selectedInstallment.idSimulacao,
      refinancingId: selectedInstallment.idContratoRefin,
    })
    onContinue?.()
  })

  useEffect(() => {
    if (data?.simulacoes?.length) {
      setSelectedInstallment(data.simulacoes[data.simulacoes.length - 1])
    }
  }, [data])

  useEffect(() => {
    if (data?.renda) {
      setNetIncome(String(Math.round(data.renda * 100)))
    }
  }, [data?.renda])

  const requiresConsent = !!simulationData?.naoRecebeViaCrefisa

  const simConsent = data?.dadosConsentimento?.[0]
  const bankDataConsentId = simConsent?.dadosConsentimentoId

  const authorizedConsent = verifiedConsent
    ? {
        nomeBanco: verifiedConsent.nomeBanco,
        agencia: verifiedConsent.agencia,
        conta: verifiedConsent.conta,
        digitoConta: verifiedConsent.digitoConta,
      }
    : simConsent && simConsent.conta
      ? {
          nomeBanco: simConsent.nomeBanco,
          agencia: simConsent.agencia,
          conta: simConsent.conta,
          digitoConta: simConsent.digitoConta,
        }
      : null
  const isConsentAuthorized = !!authorizedConsent

  useEffect(() => {
    if (simConsent && simConsent.conta) {
      const bankAccount = consentToBankAccount(simConsent);

      setConsentBankAccount(bankAccount);
      setCustomer({
        ...(bankDataConsentId ? { dataConsentId: bankDataConsentId } : {}),
        bankAccount,
      });
    }
  }, [simConsent, bankDataConsentId, setCustomer, setConsentBankAccount]);

  const handleVerifyConsent = async () => {
    if (!simulationData?.clientId) return

    try {
      setConsentLoading(true)

      const consent = await crefisaService.getConsent(simulationData.clientId)
      const item = consent.dadosConsentimento?.[0]

      if (!consent.consentimentoRealizado || !item) {
        toast({
          title: 'Consentimento pendente',
          description: consent.mensagem || 'Consentimento ainda não realizado',
        })
        return
      }

      const bankAccount = consentToBankAccount(item);

      setVerifiedConsent(item);
      setConsentBankAccount(bankAccount);
      setCustomer({
        ...(item.dadosConsentimentoId
          ? { dataConsentId: item.dadosConsentimentoId }
          : {}),
        bankAccount,
      });

      toast({
        title: 'Consentimento autorizado',
        description:
          consent.mensagem || 'Consentimento autorizado pelo cliente',
      })
    } catch (error: any) {
      toast({
        title: 'Erro ao verificar consentimento',
        description: error?.message || 'Erro ao verificar consentimento',
        variant: 'destructive',
      })
    } finally {
      setConsentLoading(false)
    }
  }

  const handleCopyConsentLink = async () => {
    if (!data?.linkConsentimento) return

    try {
      await navigator.clipboard.writeText(data.linkConsentimento)
      toast({
        title: 'Link copiado',
        description:
          'Link do consentimento copiado para a área de transferência.',
      })
    } catch {
      toast({
        title: 'Erro ao copiar',
        description: 'Não foi possível copiar o link.',
        variant: 'destructive',
      })
    }
  }

  const errorMessage =
    !isFetching && data?.sucesso === false
      ? data?.mensagemLimiteTomado || 'Não foi possível realizar a simulação.'
      : null

  return {
    form: getOffersModalForm,
    submitModal,
    isPending: isFetching,
    isFetching,
    steps,
    retry,
    cancel,
    isError,
    installments: data?.simulacoes ?? [],
    selectedInstallment,
    setSelectedInstallment,
    borrowedValue,
    commission: data?.commission,
    matricula: data?.matricula,
    digitoMatricula: data?.digitoMatricula,
    manualRegistration,
    setManualRegistration,
    manualRegistrationDigit,
    setManualRegistrationDigit,
    netIncome,
    setNetIncome,
    hasRenda,
    isNetIncomeIncomplete,
    linkConsentimento: data?.linkConsentimento,
    handleCopyConsentLink,
    errorMessage,
    parcelas: data?.installment,
    requiresConsent,
    authorizedConsent,
    isConsentAuthorized,
    consentLoading,
    handleVerifyConsent,
  }
}
